import { FC, useEffect, useMemo } from 'react';
import {
    Button,
    ButtonStyles,
    ProcessButton,
    ProcessButtonStatus,
    Table,
    TableColumn
} from '@silverfort/sf-components';

import { DeviceToken } from '../../../types/User';
import useTestToken, { TestTokenPayload } from '../../../api/mutations/useTestToken';
import { useUsers } from '../../../context/UserManagementContext';
import { usePopup } from '../../../context/PopupProvider';
import { MFA_TYPES } from '@silverfort-nodelibs/common/utils/consts';

type FooterProps = {
    primaryButtonText: string;
    handleTestAll: () => void;
    handleCancel: () => void;
};

type TestTokenButtonProps = {
    onTest: () => void;
    getState: () => {
        buttonStatus: ProcessButtonStatus;
        message?: string;
    };
    cancelTest: () => void;
};

type TestTokenRow = Pick<TestTokenPayload, 'device' | 'machineName'> &
    TestTokenButtonProps & {
        name: string;
    };

const FooterComponent: FC<FooterProps> = ({ handleTestAll, handleCancel, primaryButtonText }) => {
    return (
        <div className="flex flex-row flex-1 justify-end gap-2">
            <Button text="Close" buttonStyle={ButtonStyles.TertiarySolid} onClick={handleCancel} />
            <Button text={primaryButtonText} onClick={handleTestAll} />
        </div>
    );
};

const TestTokenButton: FC<TestTokenButtonProps> = ({ onTest, getState, cancelTest }) => {
    const { buttonStatus, message } = getState();
    return (
        <ProcessButton
            text="Test"
            onCancel={cancelTest}
            status={buttonStatus}
            onClick={onTest}
            loadingMessage={'Testing token'}
            successMessage={message || 'Success'}
            failureMessage={'Token test failed'}
            failureInfo={message}
            onRetry={onTest}
        />
    );
};

const TEST_TOKEN_TABLE_COLUMNS: TableColumn<TestTokenRow>[] = [
    {
        columnKey: 'name',
        title: 'name',
        columnClassName: '!w-[160px]'
    },
    {
        columnKey: 'device',
        title: 'device',
        columnClassName: '!w-[64px]'
    },
    {
        columnKey: 'machineName',
        title: 'machineName',
        columnClassName: '!w-[176px]'
    },
    {
        columnKey: 'onTest',
        title: 'testButton',
        columnClassName: '!w-[224px] justify-items-end',
        render: (testTokenRow) => <TestTokenButton {...testTokenRow} />
    }
];

interface UseTestTokensPopupParams {
    tokens: DeviceToken[];
    onTokenTested?: () => void;
}

interface UseTestTokensPopupReturnType {
    openTestTokensPopup: () => void;
}

type TestTokensPopup = {
    tokens: TestTokenRow[];
    isMultiple: boolean;
};

const TestTokensPopupContent: FC<TestTokensPopup> = ({ tokens, isMultiple }) => {
    return (
        <div className="flex flex-col gap-4 w-full">
            <Table
                headerClassName="hidden"
                bodyHeight={isMultiple ? 300 : 48}
                rowClassName={isMultiple ? '' : '!border-0 !border-t-0'}
                columns={TEST_TOKEN_TABLE_COLUMNS}
                rows={tokens}
            />
        </div>
    );
};

export const useTestTokensPopup = ({ tokens }: UseTestTokensPopupParams): UseTestTokensPopupReturnType => {
    const { openPopup, closePopup, updatePopup } = usePopup();
    const { users } = useUsers();

    const testTokens = useMemo(
        () =>
            tokens.reduce<TestTokenPayload[]>((testTokensPayloads, token) => {
                const user = users.find((u) => u.guid === token.userId);
                if (!user) {
                    return testTokensPayloads;
                }

                return testTokensPayloads.concat([
                    {
                        userName: user.userName,
                        domainName: user.domainName,
                        token: token.id,
                        tokenType: token.type,
                        machineName: token.machineName,
                        device: token.deviceName
                    }
                ]);
            }, []),
        [tokens, users]
    );

    const { testToken, getState, cancelTest } = useTestToken();

    const testTokensRows = useMemo(
        () =>
            testTokens.map((payload) => ({
                name:
                    Object.values(MFA_TYPES).find((tokenType) => tokenType.ID === payload.tokenType)?.DISPLAY_NAME ||
                    'N/A',
                device: payload.device,
                machineName: payload.machineName,
                onTest: () => testToken(payload),
                getState: () => getState(payload.token),
                cancelTest: () => cancelTest(payload.token)
            })),
        [testTokens, testToken, getState, cancelTest]
    );

    const testAll = () => {
        testTokensRows.forEach((token) => {
            if (token.getState()?.buttonStatus !== 'loading') {
                token.onTest();
            }
        });
    };

    const cancelAll = () => {
        testTokensRows.forEach((token) => {
            token.cancelTest();
        });
    };

    const closeTestTokenPopup = () => {
        cancelAll();
        updatePopup({
            ContentComponentProps: {
                tokens: testTokensRows
            }
        });
        closePopup();
    };

    useEffect(() => {
        updatePopup({
            ContentComponentProps: {
                tokens: testTokensRows
            }
        });
    }, [testTokensRows, getState, cancelTest]);

    const isMultiple = testTokensRows.length > 1;

    const openTestTokensPopup = () => {
        openPopup({
            title: 'Test tokens',
            popupClassName: 'w-[720px] !max-w-[720px]',
            contentClassName: isMultiple ? '' : '!py-1 !px-2',
            ContentComponent: TestTokensPopupContent,
            FooterComponent: FooterComponent,
            ContentComponentProps: {
                tokens: testTokensRows,
                isMultiple: isMultiple
            },
            FooterComponentProps: {
                primaryButtonText: isMultiple ? 'Test all' : 'Done',
                handleTestAll: isMultiple ? testAll : closeTestTokenPopup,
                handleCancel: closeTestTokenPopup
            }
        });
    };

    return {
        openTestTokensPopup
    };
};
